import * as Sentry from "@sentry/react";

/**
 * Set user context in Sentry
 * Call this when user logs in or user data is available
 */
export const setSentryUser = (user: {
  id: string | number;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}) => {
  Sentry.setUser({
    id: user.id.toString(),
    email: user.email,
    username: user.username || `${user.first_name} ${user.last_name}`.trim(),
  });
};

/**
 * Clear user context in Sentry
 * Call this when user logs out
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Add additional context to Sentry
 */
export const setSentryContext = (
  key: string,
  context: Record<string, unknown>
) => {
  Sentry.setContext(key, context);
};

/**
 * Add tags to Sentry
 */
export const setSentryTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

/**
 * Manually capture an exception
 */
export const captureException = (
  error: Error,
  context?: Record<string, unknown>
) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>);
      });
    }
    Sentry.captureException(error);
  });
};

/**
 * Manually capture a message
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, unknown>
) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>);
      });
    }
    Sentry.captureMessage(message, level);
  });
};

/**
 * Add breadcrumb for debugging
 */
export const addSentryBreadcrumb = (
  message: string,
  category: string = "manual",
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, unknown>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
};

/**
 * Profiler HOC for performance monitoring
 */
export const withSentryProfiling = Sentry.withProfiler;

/**
 * Create a higher-order component for error boundary
 */
export const withSentryErrorBoundary = Sentry.withErrorBoundary;

/**
 * Performance monitoring for API calls
 */
export const startAPITransaction = (name: string) => {
  return Sentry.startSpan(
    {
      name,
      op: "http.client",
    },
    (span) => span
  );
};

/**
 * Set up performance monitoring for a specific operation
 */
export const measurePerformance = async <T>(
  operationName: string,
  operation: () => Promise<T> | T,
  context?: Record<string, unknown>
): Promise<T> => {
  return await Sentry.startSpan(
    {
      name: operationName,
      op: "custom",
    },
    async (span) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          span.setAttribute(key, String(value));
        });
      }

      try {
        const result = await operation();
        span.setStatus({ code: 1, message: "ok" });
        return result;
      } catch (error) {
        span.setStatus({ code: 2, message: "internal_error" });
        captureException(error as Error, context);
        throw error;
      }
    }
  );
};
